import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { useNotify } from 'react-admin';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditReportService, {
    type CreditLedgerRow,
    type CustomerCreditTotals,
    type Restaurant
} from '../../services/credit/creditReportService.ts';
import { RANGE_LABELS, type RangeKey, rangeFor } from './dateRanges.ts';

const service = new CreditReportService();

const euro = (n: number | null | undefined) =>
    `€${Number(n ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * A UTC instant shown in the zone the row says it should be read in - the
 * restaurant's own. Stripping the Z and printing the digits, as this used to,
 * showed GMT: a lunchtime order in Limassol at breakfast.
 */
const when = (iso: string | null, timeZone?: string | null) => {
    if (!iso) return '—';
    try {
        return new Intl.DateTimeFormat('en-GB', {
            timeZone: timeZone || 'Asia/Nicosia',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(new Date(iso));
    } catch {
        return iso.replace('T', ' ').replace('Z', '');
    }
};

/** Earned is money going out of the business; spent is it coming back as orders. */
const TYPE_COLOUR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
    EARN: 'success',
    TRIP_BONUS: 'success',
    STREAK: 'success',
    REFERRAL: 'success',
    ADJUSTMENT: 'default',
    RESTORE: 'default',
    REDEEM: 'warning',
    EXPIRE: 'error',
    CLAWBACK: 'error'
};

type SortField = 'customerName' | 'earned' | 'spent' | 'expired' | 'balance' | 'entryCount';

const CreditReportPage = () => {
    const notify = useNotify();

    const [range, setRange] = useState<RangeKey | null>('thisMonth');
    const initial = rangeFor('thisMonth');
    const [from, setFrom] = useState<string>(initial.from);
    const [to, setTo] = useState<string>(initial.to);
    const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
    const [chosen, setChosen] = useState<Restaurant[]>([]);

    const [totals, setTotals] = useState<CustomerCreditTotals[]>([]);
    const [totalsCount, setTotalsCount] = useState(0);
    const [totalsPage, setTotalsPage] = useState(0);
    const [totalsPerPage, setTotalsPerPage] = useState(25);
    const [sortField, setSortField] = useState<SortField>('spent');
    const [sortAsc, setSortAsc] = useState(false);

    const [ledger, setLedger] = useState<CreditLedgerRow[]>([]);
    const [ledgerCount, setLedgerCount] = useState(0);
    const [ledgerPage, setLedgerPage] = useState(0);
    const [ledgerPerPage, setLedgerPerPage] = useState(25);

    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState<string | null>(null);

    const filters = useMemo(
        () => ({ from: from || null, to: to || null, restaurantIds: chosen.map((r) => r.id) }),
        [from, to, chosen]
    );

    const [restaurantsFailed, setRestaurantsFailed] = useState(false);

    useEffect(() => {
        service
            .restaurants()
            .then(setRestaurants)
            .catch(() => setRestaurantsFailed(true));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(null);
        try {
            const [t, l] = await Promise.all([
                service.totals(
                    filters,
                    totalsPage * totalsPerPage,
                    totalsPage * totalsPerPage + totalsPerPage,
                    sortField,
                    sortAsc ? 'ASC' : 'DESC'
                ),
                service.ledger(filters, ledgerPage * ledgerPerPage, ledgerPage * ledgerPerPage + ledgerPerPage)
            ]);
            setTotals(t.rows);
            setTotalsCount(t.total);
            setLedger(l.rows);
            setLedgerCount(l.total);
        } catch (e) {
            // Never render a failed report as zeroes - an empty table and a
            // broken one look identical, and only one of them is good news.
            setFailed('The credit report could not be loaded.');
            notify('Could not load the credit report', { type: 'warning' });
        } finally {
            setLoading(false);
        }
    }, [filters, totalsPage, totalsPerPage, sortField, sortAsc, ledgerPage, ledgerPerPage, notify]);

    useEffect(() => {
        load();
    }, [load]);

    const applyRange = (key: RangeKey | null) => {
        setRange(key);
        if (key) {
            const r = rangeFor(key);
            setFrom(r.from);
            setTo(r.to);
        }
        setTotalsPage(0);
        setLedgerPage(0);
    };

    const sortBy = (field: SortField) => {
        if (field === sortField) {
            setSortAsc(!sortAsc);
        } else {
            setSortField(field);
            setSortAsc(false);
        }
        setTotalsPage(0);
    };

    // Totals across the page being shown, not the whole filtered set - the API
    // pages the rows, so labelling these as the grand total would be a lie.
    const pageEarned = totals.reduce((sum, r) => sum + Number(r.earned ?? 0), 0);
    const pageSpent = totals.reduce((sum, r) => sum + Number(r.spent ?? 0), 0);
    const pageBalance = totals.reduce((sum, r) => sum + Number(r.balance ?? 0), 0);

    const headCell = (field: SortField, label: string, align: 'left' | 'right' = 'right') => (
        <TableCell align={align} sortDirection={sortField === field ? (sortAsc ? 'asc' : 'desc') : false}>
            <TableSortLabel
                active={sortField === field}
                direction={sortField === field && sortAsc ? 'asc' : 'desc'}
                onClick={() => sortBy(field)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <AccountBalanceWalletIcon />
                <Typography variant="h5">Credit report</Typography>
            </Stack>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Stack spacing={2}>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={range}
                            onChange={(_, value) => applyRange(value)}
                        >
                            {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
                                <ToggleButton key={key} value={key}>
                                    {RANGE_LABELS[key]}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                                id="credit-report-from"
                                label="From"
                                type="date"
                                size="small"
                                value={from}
                                onChange={(e) => {
                                    setFrom(e.target.value);
                                    setRange(null);
                                    setTotalsPage(0);
                                    setLedgerPage(0);
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                id="credit-report-to"
                                label="To"
                                type="date"
                                size="small"
                                value={to}
                                onChange={(e) => {
                                    setTo(e.target.value);
                                    setRange(null);
                                    setTotalsPage(0);
                                    setLedgerPage(0);
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <Autocomplete
                                multiple
                                sx={{ minWidth: 320, flex: 1 }}
                                size="small"
                                options={restaurants}
                                value={chosen}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                onChange={(_, value) => {
                                    setChosen(value);
                                    setTotalsPage(0);
                                    setLedgerPage(0);
                                }}
                                disabled={restaurantsFailed}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Restaurants"
                                        placeholder={restaurantsFailed ? 'Unavailable' : 'All restaurants'}
                                    />
                                )}
                            />
                            <Button variant="outlined" onClick={() => { setChosen([]); applyRange('thisMonth'); }}>
                                Reset
                            </Button>
                        </Stack>

                        {restaurantsFailed && (
                            <Alert severity="warning">
                                The restaurant list could not be loaded, so filtering by restaurant is unavailable.
                                Everything else on this page still works.
                            </Alert>
                        )}

                        {chosen.length > 0 && (
                            <Alert severity="info">
                                Filtered to {chosen.length} restaurant{chosen.length === 1 ? '' : 's'}. Credit that is
                                not tied to an order — admin grants, referrals and welcome credit — has no restaurant
                                and is not counted here. Clear the filter for the whole picture.
                            </Alert>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {failed && <Alert severity="error" sx={{ mb: 2 }}>{failed}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                                <Typography variant="h6">Who earned and who spent</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {totalsCount.toLocaleString('en-GB')} customer
                                    {totalsCount === 1 ? '' : 's'}
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                This page: earned {euro(pageEarned)} · spent {euro(pageSpent)} · still held{' '}
                                {euro(pageBalance)}
                            </Typography>

                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {headCell('customerName', 'Customer', 'left')}
                                        <TableCell>Phone</TableCell>
                                        {headCell('earned', 'Earned')}
                                        {headCell('spent', 'Spent')}
                                        {headCell('expired', 'Expired')}
                                        {headCell('balance', 'Balance')}
                                        {headCell('entryCount', 'Entries')}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {totals.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No credit activity in this range.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {totals.map((row) => (
                                        <TableRow key={row.customerId} hover>
                                            <TableCell>
                                                {row.customerName ?? '—'}
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {row.customerEmail}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{row.phoneNumber ?? '—'}</TableCell>
                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{euro(row.earned)}</TableCell>
                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{euro(row.spent)}</TableCell>
                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{euro(row.expired)}</TableCell>
                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{euro(row.balance)}</TableCell>
                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.entryCount}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={totalsCount}
                                page={totalsPage}
                                onPageChange={(_, p) => setTotalsPage(p)}
                                rowsPerPage={totalsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setTotalsPerPage(parseInt(e.target.value, 10));
                                    setTotalsPage(0);
                                }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                                <Typography variant="h6">Ledger</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {ledgerCount.toLocaleString('en-GB')} entr{ledgerCount === 1 ? 'y' : 'ies'}
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Amounts are signed as recorded: a redemption is negative, so the column adds up.
                            </Typography>

                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>When</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell align="right">Left on lot</TableCell>
                                        <TableCell>Restaurant</TableCell>
                                        <TableCell>Order</TableCell>
                                        <TableCell>Note</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ledger.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Nothing in this range.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {ledger.map((row) => (
                                        <TableRow key={row.id} hover>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(row.createdAt, row.timeZone)}</TableCell>
                                            <TableCell>{row.customerName ?? row.customerEmail ?? '—'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.type}
                                                    color={TYPE_COLOUR[row.type] ?? 'default'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{euro(row.amount)}</TableCell>
                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{euro(row.remaining)}</TableCell>
                                            <TableCell>{row.restaurantName ?? '—'}</TableCell>
                                            <TableCell>{row.orderNumber ?? '—'}</TableCell>
                                            <TableCell>{row.note ?? row.relatedCustomerName ?? '—'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={ledgerCount}
                                page={ledgerPage}
                                onPageChange={(_, p) => setLedgerPage(p)}
                                rowsPerPage={ledgerPerPage}
                                onRowsPerPageChange={(e) => {
                                    setLedgerPerPage(parseInt(e.target.value, 10));
                                    setLedgerPage(0);
                                }}
                                rowsPerPageOptions={[25, 50, 100]}
                            />
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
};

export default CreditReportPage;
